import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Command } from "lucide-react";
import { services } from "../../data/services";
import { useLang } from "../../i18n/LanguageContext";

const base_en = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Portfolio & Case Studies", to: "/portfolio" },
  { label: "Industries", to: "/industries" },
  { label: "Blog / Insights", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

const base_ar = [
  { label: "الرئيسية", to: "/" },
  { label: "من نحن", to: "/about" },
  { label: "الخدمات", to: "/services" },
  { label: "أعمالنا ودراسات الحالة", to: "/portfolio" },
  { label: "القطاعات", to: "/industries" },
  { label: "المدونة", to: "/blog" },
  { label: "تواصل معنا", to: "/contact" },
];

export const CommandPalette = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);

  const items = useMemo(() => {
    const svc = services.map((s) => ({
      label: lang === "en" ? s.name_en : s.name_ar,
      hint: lang === "en" ? "Service" : "خدمة",
      to: `/services/${s.slug}`,
    }));
    const base = lang === "en" ? base_en : base_ar;
    const pages = base.map((b) => ({ ...b, hint: lang === "en" ? "Page" : "صفحة" }));
    return [...pages, ...svc];
  }, [lang]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const n = q.toLowerCase();
    return items.filter((i) => i.label.toLowerCase().includes(n));
  }, [q, items]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(filtered.length - 1, a + 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
      }
      if (e.key === "Enter" && filtered[active]) {
        navigate(filtered[active].to);
        setOpen(false);
        setQ("");
        setActive(0);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered, active, navigate]);

  useEffect(() => {
    setActive(0);
  }, [q]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center pt-[12vh] px-4 animate-fade-in"
      data-testid="command-palette"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl glass-strong rounded-2xl overflow-hidden shadow-[0_0_80px_rgba(0,229,255,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-4 border-b border-hair">
          <Search className="w-4 h-4 text-mid" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "en" ? "Search pages, services…" : "ابحث في الصفحات والخدمات..."}
            className="flex-1 bg-transparent outline-none text-strong placeholder:text-faint text-sm"
            data-testid="command-input"
          />
          <kbd className="text-[10px] mono-label text-faint border border-soft rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-mid">
              {lang === "en" ? "No results." : "لا توجد نتائج."}
            </div>
          )}
          {filtered.map((i, idx) => (
            <button
              key={i.to}
              onMouseEnter={() => setActive(idx)}
              onClick={() => {
                navigate(i.to);
                setOpen(false);
                setQ("");
              }}
              data-testid={`command-item-${idx}`}
              className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition ${
                idx === active ? "bg-hover" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-1 h-6 rounded-full transition ${
                  idx === active ? "bg-cyan-400" : "bg-transparent"
                }`} />
                <span className="text-sm text-strong">{i.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] mono-label text-faint">{i.hint}</span>
                <ArrowRight className={`w-3.5 h-3.5 transition ${
                  idx === active ? "text-cyan-300 translate-x-1" : "text-faint"
                }`} />
              </div>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between px-4 py-2 border-t border-hair text-[10px] mono-label text-faint">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="border border-soft rounded px-1.5 py-0.5">↑</kbd><kbd className="border border-soft rounded px-1.5 py-0.5">↓</kbd> {lang === "en" ? "navigate" : "تنقل"}</span>
            <span className="flex items-center gap-1"><kbd className="border border-soft rounded px-1.5 py-0.5">↵</kbd> {lang === "en" ? "open" : "فتح"}</span>
          </div>
          <div className="flex items-center gap-1">
            <Command className="w-3 h-3" />
            <span>K</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
